import { queryOptions } from "@tanstack/react-query";
import api from "~/shared/api/core/api";
import { Interview, PipelineStatus, Role } from "~/shared/api/pipeline/pipeline.types";
import { Params } from "~/shared/api/types/params";
import { PaginatedResponse } from "~/shared/types/pagination.types";
import { createInfiniteQuery } from "~/shared/util/createInfiniteQuery";

export const PIPELINE_KEYS = {
  all: () => ["pipeline"] as const,
  roles: () => [...PIPELINE_KEYS.all(), "roles"] as const,
  rolesList: (params: Params) => {
    return [...PIPELINE_KEYS.roles(), params] as const;
  },
  rolesStatuses: () => [...PIPELINE_KEYS.roles(), "statuses"] as const,
  interviews: () => [...PIPELINE_KEYS.all(), "interviews"] as const,
  interviewsList: (params: Params) => {
    return [...PIPELINE_KEYS.interviews(), params] as const;
  },
};

export const PIPELINE_QUERIES = {
  rolesList: (params: Params) => {
    return queryOptions({
      queryKey: PIPELINE_KEYS.rolesList(params),
      queryFn: async ({ signal }) => {
        const response = await api.get<PaginatedResponse<Role>>("/pipeline/roles", {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
  rolesStatuses: () => {
    return queryOptions({
      queryKey: PIPELINE_KEYS.rolesStatuses(),
      queryFn: async ({ signal }) => {
        const response = await api.get<PaginatedResponse<PipelineStatus>>("/job-pipeline-statuses", {
          signal,
        });

        return response.data;
      },
    });
  },
  interviewsList: (params: Params) => {
    return queryOptions({
      queryKey: PIPELINE_KEYS.interviewsList(params),
      queryFn: async ({ signal }) => {
        const response = await api.get<PaginatedResponse<Interview>>("/pipeline/interviews", {
          ...params,
          signal,
        });

        return response.data;
      },
    });
  },
};

export function useInfinitePipelineStatusFilter(params: Record<string, unknown>) {
  return createInfiniteQuery({
    fetchFn: async (params, { signal }) => {
      const response = await api.get<PaginatedResponse<PipelineStatus>>("/job-pipeline-statuses", {
        ...params,
        signal,
      });

      return response.data;
    },
    queryKeyFn: (params: Params) => {
      return [...PIPELINE_KEYS.rolesStatuses(), params] as const;
    },
  })({
    params: {
      ...params,
    },
  });
}
